import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, MapPin, Clock, RefreshCw, AlertCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';
import { REPORT_SHIFTS } from '../constants/reportFilters';
import { useAuth } from '@/context/AuthContext';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

type PeriodType = 'daily' | 'weekly' | 'monthly';
type ReportScope = 'company' | 'location';

interface SalesFilters {
  locationId: string;
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  shift: string;
}


interface ApiSalesData {
  resumen: {
    totalVentas: number;
    ingresoTotal: number;
    ticketPromedio: number;
    periodo: {
      inicio: string;
      fin: string;
      descripcion: string;
    };
  };
  comparacion: {
    ventasVariacion: string;
    ingresosVariacion: string;
  };
  ventasPorDia: Array<{
    fecha: string;
    ventas: number;
    ingresos: number;
  }>;
  topProductos: Array<{
    productId: string;
    nombre: string;
    codigo: string;
    cantidad: number;
    ingresos: number;
  }>;
  ventasPorMetodoPago: Array<{
    metodo: string;
    cantidad: number;
    monto: number;
    porcentaje: number;
  }>;
  ventasPorTurno: Array<{
    turno: string;
    ventas: number;
    ingresos: number;
    porcentaje: number;
    ticketPromedio: number;
  }>;
  ventasPorSucursal?: Array<{
    locationId: string;
    locationName: string;
    ventasPorTurno: Array<{
      turno: string;
      ventas: number;
      ingresos: number;
      porcentaje: number;
      ticketPromedio: number;
    }>;
    totales: {
      ventas: number;
      ingresos: number;
      porcentaje: number;
      ticketPromedio: number;
    };
  }>;
}

const mockSalesData = [
  { name: 'Lun', ventas: 1200, comprobantes: 25 },
  { name: 'Mar', ventas: 1900, comprobantes: 35 },
  { name: 'Mié', ventas: 800, comprobantes: 18 },
  { name: 'Jue', ventas: 2100, comprobantes: 42 },
  { name: 'Vie', ventas: 2400, comprobantes: 48 },
  { name: 'Sáb', ventas: 3200, comprobantes: 65 },
  { name: 'Dom', ventas: 2800, comprobantes: 58 },
];


const mockSalesByLocation = [
  // Sede Principal
  { 
    locationId: 'loc-1',
    locationName: 'Sede Principal', 
    ventas: 4200, 
    comprobantes: 95, 
    ticketPromedio: 44.21,
    porcentaje: 22.4,
    turno: 'Mañana',
    turnoId: 'morning'
  },
  { 
    locationId: 'loc-1',
    locationName: 'Sede Principal', 
    ventas: 4300, 
    comprobantes: 90, 
    ticketPromedio: 47.78,
    porcentaje: 22.8,
    turno: 'Tarde',
    turnoId: 'afternoon'
  },
  // Sucursal Norte
  { 
    locationId: 'loc-2',
    locationName: 'Sucursal Norte', 
    ventas: 3100, 
    comprobantes: 68, 
    ticketPromedio: 45.59,
    porcentaje: 16.5,
    turno: 'Mañana',
    turnoId: 'morning'
  },
  { 
    locationId: 'loc-2',
    locationName: 'Sucursal Norte', 
    ventas: 3100, 
    comprobantes: 74, 
    ticketPromedio: 41.89,
    porcentaje: 16.5,
    turno: 'Tarde',
    turnoId: 'afternoon'
  },
  // Sucursal Sur
  { 
    locationId: 'loc-3',
    locationName: 'Sucursal Sur', 
    ventas: 1800, 
    comprobantes: 52, 
    ticketPromedio: 34.62,
    porcentaje: 9.6,
    turno: 'Mañana',
    turnoId: 'morning'
  },
  { 
    locationId: 'loc-3',
    locationName: 'Sucursal Sur', 
    ventas: 1300, 
    comprobantes: 37, 
    ticketPromedio: 35.14,
    porcentaje: 6.9,
    turno: 'Noche',
    turnoId: 'night'
  },
  // Franquicia Centro
  { 
    locationId: 'loc-4',
    locationName: 'Franquicia Centro', 
    ventas: 600, 
    comprobantes: 18, 
    ticketPromedio: 33.33,
    porcentaje: 3.2,
    turno: 'Mañana',
    turnoId: 'morning'
  },
  { 
    locationId: 'loc-4',
    locationName: 'Franquicia Centro', 
    ventas: 400, 
    comprobantes: 17, 
    ticketPromedio: 23.53,
    porcentaje: 2.1,
    turno: 'Tarde',
    turnoId: 'afternoon'
  }
];



export const SalesReport: React.FC = () => {
  const { user } = useAuth();
  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('daily');

  // Hook para manejar rangos de fechas por defecto
  const {
    quickRange,
    dateFrom,
    dateTo,
    setQuickRange,
    setManualDates
  } = useDateRangeDefaults('sales');

  const [filters, setFilters] = useState<SalesFilters>({
    locationId: 'all',
    dateFrom: '',
    dateTo: '',
    timeFrom: '',
    timeTo: '',
    shift: 'all'
  });

  const [salesData, setSalesData] = useState<ApiSalesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFilter = (key: keyof SalesFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setFilters(prev => ({ ...prev, locationId: defaultLocationId }));
    }
  }, [defaultLocationId]);

  // Sincronizar las fechas del hook con los filtros
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      dateFrom,
      dateTo
    }));
  }, [dateFrom, dateTo]);

  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    if (field === 'dateFrom') {
      setManualDates(value, dateTo);
    } else {
      setManualDates(dateFrom, value);
    }
  };


  const fetchSalesData = async () => {
    if (!filters.locationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.dateFrom && filters.dateTo) {
        params.append('desde', filters.dateFrom);
        params.append('hasta', filters.dateTo);
      } else {
        params.append('periodo', 'dia');
      }
      
      // Añadir filtro de turno si está seleccionado
      if (filters.shift && filters.shift !== 'all') {
        params.append('turno', filters.shift);
      }

      // Añadir filtros de hora si están seleccionados
      if (filters.timeFrom) {
        params.append('horaDesde', filters.timeFrom);
      }
      if (filters.timeTo) {
        params.append('horaHasta', filters.timeTo);
      }
      
      const response = await fetch(
        `${API_BASE_URL}/reports/sales/${filters.locationId}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const result = await response.json();
      setSalesData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
      console.error('Error fetching sales data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  }, []);

  const getFilteredLocations = () => {
    return locationsWithAll;
  };

  const calculateTotals = () => {
    if (salesData) {
      return {
        totalVentas: salesData.resumen.ingresoTotal,
        totalComprobantes: salesData.resumen.totalVentas,
        ticketPromedio: salesData.resumen.ticketPromedio
      };
    }
    
    // Si no hay datos del API, retornar valores por defecto
    return { totalVentas: 0, totalComprobantes: 0, ticketPromedio: 0 };
  };

  const { totalVentas, totalComprobantes, ticketPromedio } = calculateTotals();

  const getGroupedSalesByLocation = () => {
    // Si no hay datos del API, retornar array vacío
    if (!salesData) {
      return [];
    }

    // Si es "all" y tenemos ventasPorSucursal, usar esos datos
    if (filters.locationId === 'all' && salesData.ventasPorSucursal) {
      return salesData.ventasPorSucursal.map(sucursalData => ({
        locationId: sucursalData.locationId,
        locationName: sucursalData.locationName,
        shifts: sucursalData.ventasPorTurno.map(turnoData => ({
          turnoId: turnoData.turno.toLowerCase().replace('ñ', 'n'),
          turno: turnoData.turno,
          ventas: turnoData.ingresos,
          comprobantes: turnoData.ventas,
          ticketPromedio: turnoData.ticketPromedio,
          porcentaje: turnoData.porcentaje
        })),
        subtotal: {
          ventas: sucursalData.totales.ingresos,
          comprobantes: sucursalData.totales.ventas,
          ticketPromedio: sucursalData.totales.ticketPromedio,
          porcentaje: sucursalData.totales.porcentaje
        }
      }));
    }

    // Para un local específico, usar la lógica anterior con ventasPorTurno
    if (!salesData.ventasPorTurno) {
      return [];
    }

    // Crear estructura basada en los turnos reales de la base de datos
    const locationName = locationsWithAll.find(l => l.id === filters.locationId)?.nombre || 'Local seleccionado';
    
    // Usar los turnos reales del API
    const shifts = salesData.ventasPorTurno.map(turnoData => ({
      turnoId: turnoData.turno.toLowerCase().replace('ñ', 'n'),
      turno: turnoData.turno,
      ventas: turnoData.ingresos,
      comprobantes: turnoData.ventas,
      ticketPromedio: turnoData.ticketPromedio,
      porcentaje: turnoData.porcentaje
    }));
    
    return [{
      locationId: filters.locationId,
      locationName: locationName,
      shifts: shifts,
      subtotal: {
        ventas: salesData.resumen.ingresoTotal,
        comprobantes: salesData.resumen.totalVentas,
        ticketPromedio: salesData.resumen.ticketPromedio,
        porcentaje: 100.0
      }
    }];
  };

  const groupedSales = getGroupedSalesByLocation();


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reporte de Ventas Totales</h2>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select value={filters.locationId} onValueChange={(value) => updateFilter('locationId', value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Seleccione un local" />
                </SelectTrigger>
                <SelectContent>
                  {getFilteredLocations().map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Rango</Label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="sales"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                disabled={quickRange !== 'custom'}
                className="w-[140px]"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleDateChange('dateTo', e.target.value)}
                disabled={quickRange !== 'custom'}
                className="w-[140px]"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Turno</Label>
              <Select value={filters.shift} onValueChange={(value) => updateFilter('shift', value)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_SHIFTS.map(shift => (
                    <SelectItem key={shift.id} value={shift.id}>
                      {shift.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hora desde</Label>
              <Input
                type="time"
                value={filters.timeFrom}
                onChange={(e) => updateFilter('timeFrom', e.target.value)}
                className="w-[120px]"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hora hasta</Label>
              <Input
                type="time"
                value={filters.timeTo}
                onChange={(e) => updateFilter('timeTo', e.target.value)}
                className="w-[120px]"
              />
            </div>

            <Button
              onClick={fetchSalesData}
              disabled={loading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {loading ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center text-red-800 text-sm">
                <AlertCircle className="w-4 h-4 mr-2" />
                <span className="font-medium">Error:</span>
                <span className="ml-1">{error}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estadísticas de resumen - Resultados del reporte */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-800">Total de Ventas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-900">
              S/ {totalVentas.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-green-700">
              Suma total de ventas del local seleccionado
              {filters.shift && filters.shift !== 'all' ? ` en turno ${REPORT_SHIFTS.find(s => s.id === filters.shift)?.name}` : ''}
            </p>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-800">Comprobantes Emitidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-900">{totalComprobantes}</div>
            <p className="text-xs text-blue-700">
              Total de comprobantes generados en el período seleccionado
            </p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-purple-800">Ticket Promedio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-900">
              S/ {ticketPromedio.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-purple-700">Valor promedio por comprobante</p>
          </CardContent>
        </Card>
      </div>

      {/* Ventas por Sucursal */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            Ventas por Sucursal
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Tabla de ventas por sucursal agrupada - Ancho completo */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sucursal</TableHead>
                  <TableHead>Turno</TableHead>
                  <TableHead className="text-right">Ventas</TableHead>
                  <TableHead className="text-right">Compr.</TableHead>
                  <TableHead className="text-right">Ticket Prom.</TableHead>
                  <TableHead className="text-right">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedSales.map((locationGroup) => (
                  <>
                    {/* Header del grupo de sucursal */}
                    <TableRow key={`header-${locationGroup.locationId}`} className="bg-gray-50 border-b-2">
                      <TableCell colSpan={6} className="font-bold text-gray-800">
                        <div className="flex items-center">
                          <MapPin className="w-5 h-5 mr-2 text-gray-600" />
                          {locationGroup.locationName}
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Filas de turnos */}
                    {locationGroup.shifts.map((shift, index) => (
                      <TableRow key={`${locationGroup.locationId}-${shift.turnoId}`} className="bg-white">
                        <TableCell className="pl-8">
                          <span className="text-gray-500 text-sm">└</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm">{shift.turno}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          S/ {shift.ventas.toLocaleString('es-PE')}
                        </TableCell>
                        <TableCell className="text-right">
                          {shift.comprobantes}
                        </TableCell>
                        <TableCell className="text-right">
                          S/ {shift.ticketPromedio.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm text-blue-600">
                            {shift.porcentaje}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Fila de subtotal */}
                    <TableRow key={`subtotal-${locationGroup.locationId}`} className="bg-blue-50 border-t border-blue-200">
                      <TableCell className="pl-4 font-semibold text-blue-800">
                        Subtotal {locationGroup.locationName}
                      </TableCell>
                      <TableCell className="font-semibold text-blue-800">
                        {locationGroup.shifts.length} turnos
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        S/ {locationGroup.subtotal.ventas.toLocaleString('es-PE')}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        {locationGroup.subtotal.comprobantes}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        S/ {locationGroup.subtotal.ticketPromedio.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        {locationGroup.subtotal.porcentaje.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  </>
                ))}
                
                {/* Total General - Solo mostrar cuando hay múltiples sucursales (all selected) */}
                {filters.locationId === 'all' && groupedSales.length > 1 && (
                  <TableRow className="bg-green-100 border-t-2 border-green-300">
                    <TableCell className="font-bold text-green-800 text-lg">
                      TOTAL GENERAL
                    </TableCell>
                    <TableCell className="font-bold text-green-800">
                      {groupedSales.reduce((total, location) => total + location.shifts.length, 0)} turnos
                    </TableCell>
                    <TableCell className="text-right font-bold text-green-900 text-lg">
                      S/ {totalVentas.toLocaleString('es-PE')}
                    </TableCell>
                    <TableCell className="text-right font-bold text-green-900 text-lg">
                      {totalComprobantes}
                    </TableCell>
                    <TableCell className="text-right font-bold text-green-900 text-lg">
                      S/ {ticketPromedio.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-bold text-green-900 text-lg">
                      100.0%
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};